import { useAnalytics } from '@/contexts/analytics-context/use-analytics';
import { format, UTCDate } from '@/utils/dates';
import * as Sentry from '@sentry/react';

import { getFxRatesDateRange } from '@/contexts/data-context/get-transactions-date-range';
import { Download, RefreshCw } from 'lucide-react';
import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { useAccountAPI } from '../../../api/use-account-api';
import {
  downloadCsv,
  generateCsv,
  generateDefaultFilename,
} from '../../../design-system/data-table/csv-download/utils/generate-csv';
import { Inline } from '../../../design-system/inline/inline';
import { AccountView, CategoryView, TransactionView } from '../../../types/entity-types';
import { useEntityModalState } from '../../../utils/use-entity-modal-state';
import { getTimeSinceLastSyncFromAccounts } from '../utils/get-time-since-last-sync';
import { transactionsCsvConfig } from './table-columns/transactions-csv-config';
import { TransactionDetailsDrawerWrapper } from './transaction-details-drawer/transaction-details-drawer-wrapper';
import { TransactionTable } from './transaction-table';

export type TransactionModalView = 'transaction' | 'vendor' | 'category';

type TransactionTableWrapperProps = {
  accountId: string | null;
  transactions: TransactionView[];
  accounts: AccountView[];
  categories: CategoryView[];
};

export const TransactionTableWrapper = ({
  accountId,
  transactions,
  accounts,
  categories,
}: TransactionTableWrapperProps) => {
  const analytics = useAnalytics();

  const { selectedId, mutations: modalMutations } = useEntityModalState<TransactionModalView>({
    defaultView: 'transaction',
  });

  const [isDownloadingCSV, setIsDownloadingCSV] = useState(false);
  const [filterState, setFilterState] = useState<{
    accountId: string | null;
    transactions: TransactionView[] | null;
  }>({ accountId: null, transactions: null });

  const filteredTransactions = filterState.accountId === accountId ? filterState.transactions : null;

  const { syncAccounts: syncAccountsAPI, isSyncing } = useAccountAPI();

  const lastSyncData = getTimeSinceLastSyncFromAccounts(accounts || []);
  const lastSyncedText = lastSyncData?.text;

  const availableTransactions = accountId
    ? transactions.filter((transaction) => transaction.account?.id === accountId)
    : transactions;

  const selectedTransaction = selectedId
    ? availableTransactions.find((transaction) => transaction.id === selectedId) || null
    : null;

  const onDownloadCsv = () => {
    if (isDownloadingCSV) return;
    setIsDownloadingCSV(true);
    try {
      const transactionsToDownload = filteredTransactions !== null ? filteredTransactions : availableTransactions;
      const csvContent = generateCsv(
        transactionsToDownload,
        transactionsCsvConfig.columns,
        transactionsCsvConfig.dataTransformer,
      );
      const filename = transactionsCsvConfig.filename || generateDefaultFilename();
      downloadCsv(csvContent, filename);

      const dateRange = getFxRatesDateRange({ transactions: transactionsToDownload });

      analytics.track('transactions_export_requested', {
        account_id: accountId,
        format: 'csv',
        date_range_start: dateRange.startDate,
        date_range_end: dateRange.endDate,
        row_count: transactionsToDownload.length,
      });
    } catch (error) {
      toast.error('Failed to download CSV. Please try again later or contact support.');
      Sentry.captureException(error);
    } finally {
      setIsDownloadingCSV(false);
    }
  };

  const cachedOnFilteredRowsChange = useCallback(
    (filtered: TransactionView[]) => setFilterState({ accountId, transactions: filtered }),
    [accountId],
  );

  if (availableTransactions.length === 0) {
    return (
      <Inline justify="center" className="mt-10">
        <p className="text-base text-muted-foreground">No transaction data available</p>
      </Inline>
    );
  }

  return (
    <>
      <TransactionTable
        key={accountId ?? 'all-accounts'}
        transactions={availableTransactions}
        onSelectRow={(id) => {
          modalMutations.changeSelectedItem(id);

          const transaction = availableTransactions.find((transaction) => transaction.id === id);

          analytics.track('transaction_selected', {
            transaction_id: id,
            account_id: transaction?.account?.id,
            amount: transaction?.amount,
            currency: transaction?.currency,
            transaction_date: transaction?.fspDate
              ? format(new UTCDate(transaction?.fspDate), 'yyyy-MM-dd')
              : undefined,
            transaction_type: transaction?.type,
            vendor_id: transaction?.vendor?.id,
            vendor_name: transaction?.vendor?.name,
          });
        }}
        onFilteredRowsChange={cachedOnFilteredRowsChange}
        filters={{
          show: true,
          globalSearchConfig: { withGlobalSearch: true, placeholder: 'Search transactions' },
          columns: [
            { accessor: 'vendor', label: 'Vendor', search: { show: true, placeholder: 'Search vendor' } },
            { accessor: 'category', label: 'Category', search: { show: true, placeholder: 'Search category' } },
          ],
          enableDateRangeFilter: true,
        }}
        className="pt-0"
        tableActionButtonConfig={{
          primary: {
            variant: 'outline',
            isVisible: true,
            text: 'Export',
            onClick: onDownloadCsv,
            icon: <Download />,
            isLoading: isDownloadingCSV,
          },
          secondary: {
            isVisible: true,
            text: lastSyncedText || 'Sync All Accounts',
            onClick: () => {
              syncAccountsAPI(transactions[0].account?.company.id);
            },
            icon: <RefreshCw className="h-4 w-4" />,
            isLoading: isSyncing,
          },
        }}
        meta={{
          rowHeight: 76,
          selectedRowId: selectedTransaction?.id,
        }}
      />

      <TransactionDetailsDrawerWrapper
        selectedTransaction={selectedTransaction}
        transactions={filteredTransactions !== null ? filteredTransactions : availableTransactions}
        categories={categories}
        onCloseDrawer={() => {
          modalMutations.resetSearchParams();
        }}
        onChangeSelectedTransaction={modalMutations.changeSelectedItem}
      />
    </>
  );
};
